import { memo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { SelectItem, TmdxSelect } from '@ds';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { CASE_DEFAULT_VALUES } from '../../forms/CreateCaseForm/config';
import {
  EUROPE_REGION_ID,
  NA_REGION_ID,
  regionDefaultTimezoneById,
  serviceRegionNameById,
} from '@/constants/serviceRegion.constants';

const SERVICE_REGION_OPTIONS = [
  { value: NA_REGION_ID, label: serviceRegionNameById[NA_REGION_ID] },
  { value: EUROPE_REGION_ID, label: serviceRegionNameById[EUROPE_REGION_ID] },
] as const;

type ServiceRegionSelectorProps = {
  isDisabled?: boolean;
};

export const ServiceRegionSelector = memo(({ isDisabled = false }: ServiceRegionSelectorProps) => {
  const { control, reset, trigger, getValues } = useFormContext<CaseFormType>();

  const onServiceRegionChange = (newServiceRegionId: string) => {
    const formValues = getValues();
    const {
      donorHospitalId,
      donorHospitalName,
      hospitalCustomLocation,
      donorHospitalTimeZone,
      txCenterId,
      txCenterName,
      txCenterCustomLocation,
      txCenterTrialPrograms,
      coordinatorId,
      receivingSurgeonId,
      isServiceWithoutCoordinator,
      opoId,
      opoName,
      opoContactId,
      secondaryOpoContactId,
      opoSourceContactPhone,
      secondaryOpoContactPhone,
      reAllocationCaseId,
      ...fieldsThatRemainTheSame
    } = formValues;

    const defaultTimezone = regionDefaultTimezoneById[newServiceRegionId as keyof typeof regionDefaultTimezoneById];

    reset(
      {
        ...CASE_DEFAULT_VALUES,
        ...fieldsThatRemainTheSame,
        serviceRegionId: newServiceRegionId,
        recipientHospitalTimeZone: defaultTimezone,
        donorHospitalTimeZone: defaultTimezone,
      },
      { keepTouched: true, keepDirty: true, keepErrors: false },
    );

    if (formValues.donorId) {
      trigger('donorId');
    }
  };

  return (
    <Controller
      name="serviceRegionId"
      control={control}
      render={({ field, fieldState: { invalid, error } }) => (
        <TmdxSelect
          aria-label="Service Region"
          isDisabled={isDisabled}
          selectedKeys={field.value ? [field.value] : [NA_REGION_ID]}
          isInvalid={invalid}
          errorMessage={error?.message}
          disallowEmptySelection
          items={SERVICE_REGION_OPTIONS}
          onSelectionChange={(keys) => {
            const key = keys.currentKey as string;
            onServiceRegionChange(key);
          }}
        >
          {(item) => <SelectItem key={item.value}>{item.label}</SelectItem>}
        </TmdxSelect>
      )}
    />
  );
});
